/**
 * The result of a voice detector for one short block of audio.
 * Positions are frame numbers, counted from the start of capture. A frame holds one sample of each input.
 */
export interface VoiceActivity {
  /** The block contains speech. */
  speech: boolean
  /** Where the current speech started, or the last speech if there is none now. */
  onsetFrame: number | null
  /** Where the last speech ended: the first frame after it. */
  lastSpeechFrame: number | null
  /**
   * Where the last loud block ended: the first frame after it.
   * A loud block is louder than the speech threshold, but maybe not yet confirmed as speech.
   */
  lastLoudFrame: number | null
  /** The block contains audible sound, also if it is too quiet for speech. */
  sound: boolean
}

/**
 * Tells whether short blocks of one audio input contain speech.
 * The {@link SpeechSegmenter} uses the results to decide where utterances end. The detector does not change audio.
 */
export interface VoiceDetector {
  /**
   * Analyze the next block, which starts at position `firstFrame`.
   * With `calibrateNoise`, the detector may learn the background noise from this block.
   * Callers allow it only before the OpenAI session is ready, before the user is expected to speak.
   */
  process(samples: Float32Array, firstFrame: number, calibrateNoise: boolean): VoiceActivity
}

/**
 * Compute the RMS level of PCM samples (floats in the -1..1 range), in dBFS
 */
export function getRmsLevelDb(samples: ArrayLike<number>): number {
  let sumOfSquares = 0
  for (let i = 0; i < samples.length; i++) sumOfSquares += samples[i] * samples[i]
  const rms = Math.sqrt(sumOfSquares / Math.max(samples.length, 1))
  return 20 * Math.log10(Math.max(rms, 1e-9))
}

/** Tuning options of the {@link NoiseFloorDetector} */
export interface NoiseFloorDetectorOptions {
  /** Level above the background noise floor treated as speech, in dB */
  speechAboveNoiseDb: number
  /** Absolute minimum level of speech, in dBFS (ignores level changes in near silence) */
  minSpeechDb: number
  /** How fast the noise floor follows rising background noise, in dB per second */
  noiseFloorRiseDbPerSecond: number
  /** Time constant of the level smoothing used for the noise floor, in ms (short dips like dropouts don't count) */
  levelSmoothingMs: number
}

/** Distance the noise floor keeps from the speech threshold while speech is detected, in dB */
const SPEECH_FLOOR_MARGIN_DB = 3

/**
 * Defaults tuned by evaluating OpenAI transcription of noisy and telephone audio:
 * higher thresholds miss speech in heavy noise
 */
const defaultNoiseFloorOptions: NoiseFloorDetectorOptions = {
  speechAboveNoiseDb: 6,
  minSpeechDb: -55,
  noiseFloorRiseDbPerSecond: 0.5,
  levelSmoothingMs: 300,
}

/**
 * Detects speech by loudness, for ordinary transcription: the browser microphone, or the mixed audio of the capture app.
 * It estimates the background noise level, and treats blocks clearly louder than that as speech.
 * The noise level follows quieter audio at once, but rises only slowly,
 * and stays below continuous speech, so the speech cannot become "background noise".
 * Each loud block counts as speech at once, without confirmation.
 * A block is one browser audio callback, about 85 ms. Its duration comes from the actual sample rate.
 */
export class NoiseFloorDetector implements VoiceDetector {
  private readonly options: NoiseFloorDetectorOptions
  /** Smoothed audio power (linear) */
  private smoothedPower: number | null = null
  /** Estimated background noise level, in dBFS */
  private noiseFloorDb: number | null = null
  private speaking = false
  private onsetFrame: number | null = null
  private lastSpeechFrame: number | null = null

  constructor(
    private readonly sampleRate: number,
    options: Partial<NoiseFloorDetectorOptions> = {},
  ) {
    this.options = { ...defaultNoiseFloorOptions, ...options }
  }

  process(samples: Float32Array, firstFrame: number): VoiceActivity {
    const { options } = this
    const levelDb = getRmsLevelDb(samples)
    const frameMs = (samples.length / this.sampleRate) * 1000

    // Smooth the level in the power domain, so short dips (e.g. dropouts) don't count as background noise
    const power = 10 ** (levelDb / 10)
    const smoothing = frameMs / (options.levelSmoothingMs + frameMs)
    this.smoothedPower =
      this.smoothedPower === null ? power : this.smoothedPower + smoothing * (power - this.smoothedPower)
    // Levels below the speech threshold margin are all quiet enough (e.g. digital silence of muted audio)
    const smoothedDb = Math.max(10 * Math.log10(this.smoothedPower), options.minSpeechDb - options.speechAboveNoiseDb)

    // Track the noise floor: follow the smoothed level down immediately
    if (this.noiseFloorDb === null || smoothedDb < this.noiseFloorDb) this.noiseFloorDb = smoothedDb

    const isSpeech = levelDb > Math.max(this.noiseFloorDb + options.speechAboveNoiseDb, options.minSpeechDb)

    // Rise slowly with the background noise. During speech, stay clearly below the speech threshold,
    // so sustained speech can't lift the floor to its own level and suppress itself,
    // while speech-like background noise (e.g. other voices in the room) can still raise it
    const risenFloorDb = this.noiseFloorDb + (options.noiseFloorRiseDbPerSecond * frameMs) / 1000
    const maxFloorDb = isSpeech ? smoothedDb - options.speechAboveNoiseDb - SPEECH_FLOOR_MARGIN_DB : smoothedDb
    this.noiseFloorDb = Math.max(this.noiseFloorDb, Math.min(risenFloorDb, maxFloorDb))

    if (isSpeech) {
      if (!this.speaking) this.onsetFrame = firstFrame
      this.lastSpeechFrame = firstFrame + samples.length
    }
    this.speaking = isSpeech
    return {
      speech: isSpeech,
      onsetFrame: this.onsetFrame,
      lastSpeechFrame: this.lastSpeechFrame,
      lastLoudFrame: this.lastSpeechFrame,
      sound: levelDb > options.minSpeechDb,
    }
  }
}

/** The speaker channels of the capture app always have 48 kHz samples. */
const CHANNEL_SAMPLE_RATE = 48000
const WINDOW_SIZE = 512
// At 48 kHz, each frequency bin spans 93.75 Hz. Analyze 375–4031 Hz to reduce low-frequency microphone noise.
const FIRST_BIN = 4
const LAST_BIN = 43
// Precompute the Blackman window and frequency coefficients for the fixed speech band.
const weights = Array.from({ length: LAST_BIN - FIRST_BIN + 1 }, (_, index) => {
  const bin = index + FIRST_BIN
  return Array.from({ length: WINDOW_SIZE }, (_, sample) => {
    const angle = (2 * Math.PI * sample) / WINDOW_SIZE
    const window = 0.42 - 0.5 * Math.cos(angle) + 0.08 * Math.cos(2 * angle)
    return [window * Math.cos(bin * angle), window * Math.sin(bin * angle)]
  })
})
/** Lowest background noise level, in dB. Also the level before calibration. */
const MIN_NOISE_DB = -61
/** A block is loud when it is this much louder than the background noise, in dB... */
const SPEECH_ABOVE_NOISE_DB = 6
/** ...and at least this loud, in dB. */
const MIN_SPEECH_DB = -55
/** Levels below this are zero-filled capture gaps, not background noise, in dB. */
const GAP_DB = -120
/** RMS level of audible sound, also too quiet for speech, in dBFS. */
const MIN_SOUND_DB = -65
/** Duration of microphone audio that sets the background noise level, in ms. */
const CALIBRATION_MS = 500
/** Speech continues through quiet gaps up to this long, like between words: 80 ms at 48 kHz. */
const SPEECH_HOLD_FRAMES = 3840

/**
 * Detects speech in one speaker channel of the capture app: the separate microphone or system audio.
 * Each call analyzes 10 ms of 48 kHz audio.
 * For the microphone, it measures the loudest frequency in the speech range (375–4031 Hz).
 * So low hum, like fan or traffic noise, counts less. For system audio, it measures plain loudness,
 * because played-back audio is usually clean.
 * Speech is confirmed when three of the last five blocks are loud. Speech continues through gaps up to 80 ms,
 * like between words. These values are lower than in common detectors (e.g. the Hark library, with 500 ms),
 * so that quiet, short replies like "mhm" are detected.
 * Before the OpenAI session is ready, the first 500 ms of microphone audio set the background noise level.
 */
export class SpeakerChannelDetector implements VoiceDetector {
  private readonly samples = new Float32Array(WINDOW_SIZE)
  private readonly history: { loud: boolean; frame: number }[] = []
  private calibration: number[] = []
  private calibratedMs = 0
  private noiseDb = MIN_NOISE_DB
  private speaking = false
  private onsetFrame: number | null = null
  private lastSpeechFrame: number | null = null
  private lastLoudFrame: number | null = null

  /** Select frequency-based microphone detection or RMS-based system-audio detection. */
  constructor(private readonly microphone: boolean) {}

  /**
   * Analyze the next audio block at `firstFrame` on the shared capture timeline.
   * Allow startup noise calibration only before the transcription session is ready,
   * so user speech cannot become the initial background level.
   * Return confirmed speech positions separately from raw above-threshold (loud) positions:
   * a loud block can be the start of a word, so it must postpone the end of an utterance.
   * `sound` also reports audible sound below the speech threshold, which the segmenter commits after a while.
   */
  process(samples: Float32Array, firstFrame: number, calibrateNoise = true): VoiceActivity {
    const levelDb = this.microphone ? this.speechBandLevelDb(samples) : getRmsLevelDb(samples)
    const frameMs = samples.length / (CHANNEL_SAMPLE_RATE / 1000)
    // Ignore zero-filled capture gaps when learning the microphone noise level.
    if (calibrateNoise && this.microphone && this.calibratedMs < CALIBRATION_MS && levelDb > GAP_DB) {
      this.calibration.push(levelDb)
      this.calibratedMs += frameMs
      if (this.calibratedMs >= CALIBRATION_MS) {
        this.calibration.sort((a, b) => a - b)
        this.noiseDb = Math.max(MIN_NOISE_DB, this.calibration[Math.floor(this.calibration.length / 2)])
        this.calibration = []
      }
    }
    const loud = levelDb > Math.max(MIN_SPEECH_DB, this.noiseDb + SPEECH_ABOVE_NOISE_DB)
    // Follow quiet background changes. Speech must not raise its own threshold.
    if (!loud && levelDb > GAP_DB) {
      this.noiseDb += Math.min(1, frameMs / CALIBRATION_MS) * (Math.max(MIN_NOISE_DB, levelDb) - this.noiseDb)
    }
    this.history.push({ loud, frame: firstFrame })
    if (this.history.length > 5) this.history.shift()
    if (loud) {
      this.lastLoudFrame = firstFrame + samples.length
      if (!this.speaking && this.history.filter(frame => frame.loud).length >= 3) {
        this.speaking = true
        this.onsetFrame = this.history.find(frame => frame.loud)!.frame
      }
      if (this.speaking) this.lastSpeechFrame = this.lastLoudFrame
    } else if (this.lastLoudFrame === null || firstFrame + samples.length - this.lastLoudFrame >= SPEECH_HOLD_FRAMES) {
      this.speaking = false
    }
    return {
      speech: this.speaking,
      onsetFrame: this.onsetFrame,
      lastSpeechFrame: this.lastSpeechFrame,
      lastLoudFrame: this.lastLoudFrame,
      sound: getRmsLevelDb(samples) > MIN_SOUND_DB,
    }
  }

  /**
   * Return the level of the loudest frequency in the speech band, in dB.
   * Use the latest 512 samples, also from previous blocks.
   */
  private speechBandLevelDb(samples: Float32Array) {
    const count = Math.min(samples.length, WINDOW_SIZE)
    this.samples.copyWithin(0, count)
    this.samples.set(samples.subarray(samples.length - count), WINDOW_SIZE - count)
    // Compute only speech-band bins. This small transform needs no audio library.
    let peak = 0
    for (const bin of weights) {
      let real = 0,
        imaginary = 0
      for (let index = 0; index < WINDOW_SIZE; index++) {
        real += this.samples[index] * bin[index][0]
        imaginary += this.samples[index] * bin[index][1]
      }
      peak = Math.max(peak, Math.hypot(real, imaginary) / WINDOW_SIZE)
    }
    return 20 * Math.log10(Math.max(peak, 1e-9))
  }
}
